import {Component, inject, signal} from '@angular/core';
import {
    RegistrationNewModuleService,
    SettingsService,
    SubtitlesService,
    ViewsService
} from "../../core";
import {MenuButtonComponent} from "./menu-button/menu-button.component";
import {
    RegistrationButtonComponent
} from "./registration-button/registration-button.component";
import {ModuleData, ModuleLayout, ModuleMenuOption, Settings} from "../../models";

@Component({
    selector: 'settings-menu',
    standalone: true,
    imports: [MenuButtonComponent, RegistrationButtonComponent],
    templateUrl: './settings-menu.component.html',
    styleUrl: './settings-menu.component.scss'
})
export class SettingsMenuComponent {
    registrationNewModule = inject(RegistrationNewModuleService);
    settings = inject(SettingsService);
    private subtitlesService = inject(SubtitlesService);
    private views = inject(ViewsService);

    protected readonly showModulesList = signal(false);
    private readonly openedModuleUdid = signal<string | null>(null);

    protected readonly labels = {
        account: this.subtitlesService.setSubtitle(5041),
        modules: this.subtitlesService.setSubtitle(5223),
        moduleInfo: this.subtitlesService.setSubtitle(1188),
        controllerSoftware: this.subtitlesService.setSubtitle(60),
        additionalSoftware: 'Additional software',
        notifications: this.subtitlesService.setSubtitle(1096),
        tiles: this.subtitlesService.setSubtitle(5040),
        moduleOptions: this.subtitlesService.setSubtitle(8054),
        registration: this.subtitlesService.setSubtitle(1319),
        manuals: this.subtitlesService.setSubtitle(6537),
    };

    protected readonly moduleOptions: readonly ModuleMenuOption[] = [{
        layout: 'module_info',
        label: this.labels.moduleInfo,
        isVisible: () => true,
    }, {
        layout: 'controller_software',
        label: this.labels.controllerSoftware,
        isVisible: module => module.company === 'polypipe',
    }, {
        layout: 'additional_software',
        label: this.labels.additionalSoftware,
        isVisible: module => module.company === 'polypipe',
    }, {
        layout: 'notifications',
        label: this.labels.notifications,
        isVisible: module => module.type !== 'zones_controller',
    }, {
        layout: 'tiles',
        label: this.labels.tiles,
        isVisible: module => module.type !== 'zones_controller'
            && module.type !== 'zones_system_controller',
    }, {
        layout: 'module_options',
        label: this.labels.moduleOptions,
        isVisible: () => true,
    }];

    isModuleOpen(module: ModuleData): boolean {
        return module.udid === this.openedModuleUdid();
    }

    toggleModulesList(): void {
        this.showModulesList.update(isOpen => !isOpen);

        if (!this.showModulesList()) {
            this.openedModuleUdid.set(null);
        }
    }

    toggleModuleOptions(module: ModuleData): void {
        this.openedModuleUdid.update(udid => udid === module.udid ? null : module.udid);
    }

    isModuleButtonActive(state: Settings, module: ModuleData): boolean {
        return this.settings.isActive(state)
            && !this.views.isMobileView()
            && module.udid === this.settings.selectedModule?.udid;
    }

    isAccountMenuButtonActive(state: Settings): boolean {
        return this.settings.isActive(state) && !this.views.isMobileView();
    }

    switchMainLayout(page: 'account' | 'manuals'): void {
        this.showModulesList.set(false);
        this.openedModuleUdid.set(null);
        this.settings.switchLayout(page);
    }

    switchLayout(udid: string, page: ModuleLayout): void {
        this.settings.selectModuleByUdid(udid);
        this.settings.switchLayout(page);
    }

    openRegistrationForm(): void {
        if (this.registrationNewModule.prepareRegistrationForm()) {
            this.settings.switchLayout('registration_new_module');
        }
    }
}
